import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FolderIcon, DocumentIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getApiBase } from '../api/client';

type FolderContent = {
  folders: { name: string }[];
  files: { name: string; size: number; modified?: number }[];
  path: string;
  is_folder: boolean;
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ShareViewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<FolderContent | null>(null);
  const [subPath, setSubPath] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Enlace no válido');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const q = subPath ? `?subpath=${encodeURIComponent(subPath)}` : '';
    fetch(`${getApiBase()}/api/v1/files/shared/${token}${q}`)
      .then((res) => {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          return res.json();
        }
        if (res.ok) {
          window.location.href = `${getApiBase()}/api/v1/files/shared/${token}`;
          return null;
        }
        throw new Error(res.status === 404 ? 'Enlace no encontrado o expirado' : 'Error al cargar');
      })
      .then((data) => {
        if (data) setContent(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Error');
        setContent(null);
        setLoading(false);
      });
  }, [token, subPath]);


  const loadSubFolder = (folderName: string) => {
    setSubPath(subPath ? `${subPath}/${folderName}` : folderName);
  };

  const fileUrl = (fileName: string, disposition: 'inline' | 'attachment' = 'attachment') =>
    `${getApiBase()}/api/v1/files/shared/${token}/file?path=${encodeURIComponent(subPath ? `${subPath}/${fileName}` : fileName)}&disposition=${disposition}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-600">Cargando…</p>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 max-w-md text-center">
          <p className="text-red-600 font-medium">{error || 'Enlace no encontrado o expirado'}</p>
          <p className="text-slate-500 text-sm mt-2">El enlace puede haber caducado o no ser válido.</p>
        </div>
      </div>
    );
  }

  if (!content.is_folder) {
    return null;
  }

  const parts = subPath ? subPath.split('/').filter(Boolean) : [];
  const breadcrumb = content.path ? [content.path, ...parts] : parts;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold text-slate-800 mb-4">Contenido compartido</h1>
        {breadcrumb.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 text-sm text-slate-600 mb-4">
            <button type="button" onClick={() => setSubPath('')} className="hover:text-emerald-600">
              Raíz
            </button>
            {parts.map((part, i) => {
              const pathSoFar = parts.slice(0, i + 1).join('/');
              return (
                <span key={pathSoFar}>
                  <span className="mx-1">/</span>
                  <button type="button" onClick={() => setSubPath(pathSoFar)} className="hover:text-emerald-600">
                    {part}
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {content.folders.map((f) => (
              <li key={f.name}>
                <button
                  type="button"
                  onClick={() => loadSubFolder(f.name)}
                  className="w-full flex items-center gap-2 p-3 text-left hover:bg-slate-50"
                >
                  <FolderIcon className="h-6 w-6 text-amber-500 shrink-0" />
                  <span className="font-medium text-slate-800">{f.name}</span>
                </button>
              </li>
            ))}
            {content.files.map((file) => (
              <li key={file.name} className="flex items-center justify-between gap-4 p-3 hover:bg-slate-50">
                <span className="flex items-center gap-2 min-w-0">
                  <DocumentIcon className="h-6 w-6 text-slate-400 shrink-0" />
                  <span className="text-slate-800 truncate">{file.name}</span>
                  <span className="text-xs text-slate-500 shrink-0">{formatSize(file.size)}</span>
                </span>
                <a
                  href={fileUrl(file.name, 'attachment')}
                  download={file.name}
                  className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Descargar
                </a>
              </li>
            ))}
          </ul>
          {content.folders.length === 0 && content.files.length === 0 && (
            <p className="p-6 text-center text-slate-500 text-sm">Esta carpeta está vacía.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareViewPage;
